
/**
 * Enriches a permit object with documentation links (manual, electro, technical)
 * by looking up the component/subline in the provided hierarchy or global library.
 * 
 * @param {Object} permit - The permit/workPlan object to enrich.
 * @param {Object} hierarchyData - The full hierarchy data (containing locations/lines/sublines).
 * @param {Object} globalLibrary - Optional global library for direct component lookups.
 * @returns {Object} Enriched permit with .manualDocLink, .electroDocLink, .technischDocLink, .sublineDocLink
 */
export const enrichPermitWithLinks = (permit, hierarchyData, globalLibrary) => {
    if (!permit || !hierarchyData) return permit;

    let sublineLink = null;
    let electroLink = null;
    let manualLink = null;
    let docLink = null; // often same as manual

    try {
        const { location, hall, line, subline, component, instanceName } = permit;

        // 1. Navigate Hierarchy to find Target Object (Subline or Line)
        // Adjust for data structure: might be hierarchy[hall][line]...
        // Some data structures: hierarchy is keys -> halls.

        let targetObj = null;
        if (hall && line) {
            if (subline) {
                targetObj = hierarchyData[hall]?.[line]?.[subline];
            } else {
                targetObj = hierarchyData[hall]?.[line];
            }
        }

        if (targetObj) {
            // 2. Extract Subline Links (Baseline)
            sublineLink = targetObj.docLink || null;
            electroLink = targetObj.docLinkElectro || targetObj.electroDocLink || null;
            manualLink = targetObj.docLinkManual || targetObj.docLink || null;

            // 3. Extract Component Links (Overrides)
            if (targetObj._componentData) {
                const compType = component || permit.assetId;
                const instName = instanceName;

                let compData = null;
                if (instName && targetObj._componentData[instName]) {
                    compData = targetObj._componentData[instName];
                } else if (compType && targetObj._componentData[compType]) {
                    compData = targetObj._componentData[compType];
                }

                if (compData) {
                    if (compData.docLink) manualLink = compData.docLink;
                    if (compData.docLinkManual) manualLink = compData.docLinkManual;
                    if (compData.electroDocLink) electroLink = compData.electroDocLink;
                    if (compData.technischDocLink) sublineLink = compData.technischDocLink;
                    docLink = compData.docLink || compData.docLinkManual || null;
                }
            }
        }
    } catch (err) {
        console.error("Error enriching permit links:", err);
    }

    return {
        ...permit,
        sublineDocLink: sublineLink,
        electroDocLink: electroLink,
        manualDocLink: manualLink,
        componentDocLink: docLink || manualLink,
        technischDocLink: sublineLink,
        _enriched: true
    };
};
